import { useMemo } from "react";

// Generations 01-03 are the founding du Pont line.
// Any surname with members in these generations is an "original branch."
const ORIGINAL_GENERATIONS = new Set(["01", "02", "03"]);

/**
 * Extracts a 4-digit year from strings like "15 January 1950" or "1950".
 */
function parseYear(dateStr) {
  if (!dateStr) return null;
  const match = dateStr.match(/\b(\d{4})\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Normalises a surname for grouping.
 * Merges "du Pont [de Nemours]" into "du Pont" so the founding family
 * isn't split into two separate entries.
 */
function normaliseKey(name) {
  let cleaned = name.trim();
  // Strip the "[de Nemours]" bracket suffix used on the earliest ancestors
  cleaned = cleaned.replace(/\s*\[de Nemours\]/i, "");
  return cleaned.toLowerCase();
}

/**
 * Builds a Map of id -> node for O(1) lookups.
 */
function buildNodeMap(tree) {
  const map = new Map();
  for (const node of tree) {
    map.set(String(node.id), node);
  }
  return map;
}

/**
 * Walks up the father chain to find the earliest ancestor with this surname.
 * Stops when the father has a different surname, isn't in the data, or
 * we detect a cycle.
 */
function findOrigin(node, surnameKey, nodeMap) {
  const visited = new Set();
  let current = node;

  while (current) {
    visited.add(String(current.id));

    const fatherId = current.rels?.father;
    if (!fatherId) break;
    if (visited.has(String(fatherId))) break;

    const father = nodeMap.get(String(fatherId));
    if (!father) break;

    const fatherKey = normaliseKey(father.data?.last_name || "");
    if (fatherKey !== surnameKey) break;

    current = father;
  }

  return current;
}

/**
 * Processes the flat family-tree array into two groups:
 *   - originalBranches: surnames with members in generations 01-03
 *   - extendedBranches: every other surname (married-in families)
 *
 * Also returns summary stats and respects search/sort controls.
 */
export default function useSurnameBranches(tree, search, sortOrder) {
  return useMemo(() => {
    if (!tree || tree.length === 0) {
      return {
        originalBranches: [],
        extendedBranches: [],
        totalPeople: 0,
        totalSurnames: 0,
        noSurnameCount: 0,
      };
    }

    const nodeMap = buildNodeMap(tree);

    // --- Step 1: Group nodes by normalised surname ---
    const groups = new Map();
    let noSurnameCount = 0;

    for (const node of tree) {
      const lastName = (node.data?.last_name || "").trim();
      if (!lastName) {
        noSurnameCount++;
        continue;
      }

      const key = normaliseKey(lastName);

      if (!groups.has(key)) {
        groups.set(key, {
          displayCounts: new Map(),
          members: [],
          hasOriginalGeneration: false,
        });
      }

      const group = groups.get(key);
      group.members.push(node);

      // Track capitalisation variants (ignoring the [de Nemours] bracket)
      const displayVariant = lastName.replace(/\s*\[de Nemours\]/i, "").trim();
      const count = group.displayCounts.get(displayVariant) || 0;
      group.displayCounts.set(displayVariant, count + 1);

      // Check if this person is in an original generation
      // Normalise to 2-digit string so "1", "01", and 1 all match
      const gen = node.data?.generation;
      if (gen) {
        const normalised = String(gen).padStart(2, "0");
        if (ORIGINAL_GENERATIONS.has(normalised)) {
          group.hasOriginalGeneration = true;
        }
      }
    }

    // --- Step 2: Build branch objects ---
    const allBranches = [];

    for (const [key, group] of groups) {
      // Pick the most-common display form
      let displayName = "";
      let maxCount = 0;
      for (const [variant, count] of group.displayCounts) {
        if (count > maxCount) {
          maxCount = count;
          displayName = variant;
        }
      }

      // Sort members by birth year (unknown → end)
      const members = group.members
        .map((node) => ({
          id: node.id,
          firstName: node.data?.first_name || "",
          lastName: node.data?.last_name || "",
          suffix: node.data?.numeric_suffix || "",
          birthYear: parseYear(node.data?.birthday),
          deathYear: parseYear(node.data?.death),
          sex: node.data?.sex,
          generation: node.data?.generation || null,
        }))
        .sort((a, b) => {
          if (a.birthYear == null && b.birthYear == null) return 0;
          if (a.birthYear == null) return 1;
          if (b.birthYear == null) return -1;
          return a.birthYear - b.birthYear;
        });

      // Find origin person(s)
      const originSet = new Map();
      for (const node of group.members) {
        const origin = findOrigin(node, key, nodeMap);
        const originId = String(origin.id);
        if (!originSet.has(originId)) {
          originSet.set(originId, {
            id: origin.id,
            firstName: origin.data?.first_name || "",
            lastName: origin.data?.last_name || "",
            suffix: origin.data?.numeric_suffix || "",
            birthYear: parseYear(origin.data?.birthday),
          });
        }
      }

      const origins = Array.from(originSet.values()).sort((a, b) => {
        if (a.birthYear == null && b.birthYear == null) return 0;
        if (a.birthYear == null) return 1;
        if (b.birthYear == null) return -1;
        return a.birthYear - b.birthYear;
      });

      // Figure out the generation span for display
      const gens = group.members
        .map((n) => n.data?.generation)
        .filter(Boolean)
        .sort();
      const earliestGen = gens.length > 0 ? gens[0] : null;
      const latestGen = gens.length > 0 ? gens[gens.length - 1] : null;

      allBranches.push({
        key,
        displayName,
        count: members.length,
        members,
        origins,
        multipleOrigins: origins.length > 1,
        isOriginal: group.hasOriginalGeneration,
        earliestGen,
        latestGen,
      });
    }

    // --- Step 3: Split into original vs extended ---
    const original = allBranches.filter((b) => b.isOriginal);
    const extended = allBranches.filter((b) => !b.isOriginal);

    // --- Step 4: Filter by search ---
    const searchLower = (search || "").trim().toLowerCase();
    const filterFn = (b) =>
      b.displayName.toLowerCase().includes(searchLower);

    const filteredOriginal = searchLower
      ? original.filter(filterFn)
      : original;
    const filteredExtended = searchLower
      ? extended.filter(filterFn)
      : extended;

    // --- Step 5: Sort ---
    const sortFn = (a, b) => {
      if (sortOrder === "alpha") {
        return a.displayName.localeCompare(b.displayName);
      }
      return b.count - a.count;
    };

    return {
      originalBranches: [...filteredOriginal].sort(sortFn),
      extendedBranches: [...filteredExtended].sort(sortFn),
      totalPeople: tree.length - noSurnameCount,
      totalSurnames: allBranches.length,
      noSurnameCount,
    };
  }, [tree, search, sortOrder]);
}
